// Pure helpers for the Report Production view.

// Routing code → display name (from Priority ERP routing table)
export const ROUTING_DISPLAY_NAMES = {
  'ARTASS': 'Art Assembly',
  'Assemb': 'Warehouse Assembly',
  'Blocks': 'Photo Blocks',
  'Byprod': 'Byproducts',
  'CNCBLO': 'CNC Blocks',
  'FINAL': 'Final Assembly',
  'FRAME': 'Frames',
  'Issue': 'Raw Materials',
  'MOUNT': 'Mounts',
  'PRINT': 'Print Image',
  'Phanto': 'Phantoms',
  'RMBDL': 'RM Bundle',
  'RMFRAM': 'RM Frames',
  'RMMOUN': 'RM Mounts'
};

// Idempotency key for a submission attempt. The Edge Function stamps it on the Priority
// report header (AFORM.DETAILS, CHAR 24), so a resubmission after an unknown outcome can
// find the report the first attempt created instead of reporting production twice.
export const makeBatchKey = () => {
  const raw = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;
  return `DP-${raw.replace(/[^a-zA-Z0-9]/g, '').slice(0, 16)}`;
};

// Sum of planned item quantities for a list of work orders (for filter counts)
export const sumItems = (list) => list.reduce((sum, wo) => sum + (Number(wo.quantity) || 0), 0);
export const sumReported = (list) => list.reduce((sum, wo) => sum + (Number(wo.reportedQuantity) || 0), 0);
export const sumRemaining = (list) => list.reduce(
  (sum, wo) => sum + Math.max(0, (Number(wo.quantity) || 0) - (Number(wo.reportedQuantity) || 0)),
  0
);

// Colour group for a work order's part, so staff can make one colour at a time. The key
// is the part code without its size / variant segments (RM-FR-MN-BL-2420 -> RM-FR-MN-BL,
// RM-MT-A-BL-RM05 -> RM-MT-A-BL, PB-WH-64-LAM -> PB-WH); the label is the description
// up to the size ("Mono - Black 24x20 Readymade Frame" -> "Mono - Black").
const PHOTO_BLOCK_COLOURS = { BL: 'Black', WH: 'White' };
const VARIANT_SEGMENTS = new Set(['SMPL', 'LAM', 'BESPOKE']);

export const colourGroupFor = (wo) => {
  const partName = String(wo.partName || '').trim().toUpperCase();
  const description = String(wo.partDescription || '').trim();
  const segments = partName.split('-').filter(Boolean);
  const kept = [];
  for (const seg of segments) {
    if (/\d/.test(seg) || VARIANT_SEGMENTS.has(seg)) break;
    kept.push(seg);
  }
  const key = kept.length >= 2 ? kept.join('-') : (partName || description.toUpperCase());

  if (kept[0] === 'PB' && PHOTO_BLOCK_COLOURS[kept[1]]) {
    return { key, label: `Photo Block - ${PHOTO_BLOCK_COLOURS[kept[1]]}` };
  }
  const label = description
    .replace(/(^|\s+)(\d+(\.\d+)?\s*x\s*\d+(cm|mm)?|A\d)\b.*$/i, '')
    .replace(/\s*[-–]\s*$/, '')
    .trim();
  return { key, label: label || key || 'Other' };
};

// Natural compare for part codes so sizes run 108, 1210, 2420 rather than 108, 2420, 1210.
export const comparePartNames = (a, b) =>
  String(a || '').localeCompare(String(b || ''), 'en', { numeric: true, sensitivity: 'base' });

// Format date for display
export const formatShortDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString();
  } catch {
    return dateStr;
  }
};

export const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

// Reported as a percentage of planned, capped at 100
export const progressPct = (reported, planned) => (planned > 0 ? Math.min(100, Math.round((reported / planned) * 100)) : 0);

// Local calendar-day key for a due date, so work orders due the same day compare equal.
export const dueDateKey = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

// Default "To Report" quantity: what is still outstanding. 0 is a real value (fully
// reported), so it must not fall back to the planned quantity.
export const remainingFor = (wo) => {
  const remaining = wo.remainingQuantity ?? ((Number(wo.quantity) || 0) - (Number(wo.reportedQuantity) || 0));
  return Math.max(0, remaining);
};

// Routings whose work orders make frames or mounts. Their rows show Priority's current
// stock of the part (or ask for a shelf count when reporting), so the shelf can be checked
// against it - a mismatch means earlier production was mis-counted when it was reported.
// Photo Blocks never take part, whatever routing they come through on.
export const STOCK_ROUTINGS = new Set(['RMFRAM', 'FRAME', 'RMMOUN', 'MOUNT', 'RMBDL', 'DEPHOT', 'DEPHC']);

export const showsStock = (wo) => STOCK_ROUTINGS.has(String(wo.processName || '').toUpperCase())
  && !String(wo.partName || '').toUpperCase().startsWith('PB-');

// Bespoke parts (e.g. RM-MT-A-WH-BESPOKE) are made to size, aren't kept on a shelf and have
// no barcode in Priority, so they show stock but are never shelf-counted.
export const isBespoke = (partName) => String(partName || '').toUpperCase().includes('BESPOKE');
export const asksShelfCount = (wo) => showsStock(wo) && !isBespoke(wo.partName);

// Distinct part numbers to look stock up for, sorted so the list is stable across renders.
export const stockPartsFor = (wos) =>
  [...new Set(wos.filter(showsStock).map(wo => wo.partName).filter(Boolean))].sort();

// Recompute "To Report" quantities after the work order list changes (e.g. someone else
// reported production and realtime delivered new remaining quantities).
//   - Untouched rows always follow the latest remaining quantity.
//   - Rows the user edited keep their value, except that if the remaining quantity
//     dropped (someone else reported) and the edit now exceeds it, it is clamped down
//     so the stale figure can't be over-reported.
// `prevRemaining` is serial -> remaining quantity from the previous list.
// Serials no longer present are dropped.
export const mergeQuantities = (prevQuantities, workOrders, touched, prevRemaining = {}) => {
  const next = {};
  workOrders.forEach(wo => {
    const serial = wo.serialName;
    const remaining = remainingFor(wo);
    const current = prevQuantities[serial];

    if (!touched.has(serial) || current === undefined) {
      next[serial] = remaining;
      return;
    }

    const remainingDropped = prevRemaining[serial] !== undefined && remaining < prevRemaining[serial];
    next[serial] = remainingDropped && current !== '' && Number(current) > remaining
      ? remaining
      : current;
  });
  return next;
};

// serial -> remaining quantity, used as `prevRemaining` for the next merge
export const remainingBySerial = (workOrders) => {
  const map = {};
  workOrders.forEach(wo => { map[wo.serialName] = remainingFor(wo); });
  return map;
};

// Photo Blocks packing and MDF usage, keyed by block size with the smaller side first
// (Priority's descriptions say 12x17 where the floor says 17x12). perSheet is blocks per
// 2440x1220x25 MDF sheet, from Priority's kit quantities as of 2026-10-13 (e.g. WO262024:
// 1.95 SHT for 232 blocks); it's for information only - Priority's kit lines decide what
// is actually issued.
const PHOTO_BLOCK_SIZES = {
  '10x15': { label: '15x10', perBox: 84, perSheet: 162 },
  '12x12': { label: '12x12', perBox: 80, perSheet: 162 },
  '12x17': { label: '17x12', perBox: 58, perSheet: 119 },
  '15x20': { label: '20x15', perBox: 40, perSheet: 86 }
};

// MDF board each block colour (PB-BL-*, PB-WH-*) is cut from, per Priority's kit lines.
const PHOTO_BLOCK_BOARDS = {
  BL: { code: 'MDF-BLK/WHT-2S-25MM', name: 'Black / White 25mm' },
  WH: { code: 'MDF-WHITE-2S-25MM', name: 'White 25mm' }
};

const photoBlockSize = (wo) => {
  if (!/^PB-/i.test(wo.partName || '')) return null;
  const match = /(\d+)\s*x\s*(\d+)/i.exec(wo.partDescription || '');
  if (!match) return null;
  const [short, long] = [Number(match[1]), Number(match[2])].sort((a, b) => a - b);
  return PHOTO_BLOCK_SIZES[`${short}x${long}`] || null;
};

// "4 boxes + 12 loose" style text for full boxes and leftover blocks.
export const formatBoxes = (fullBoxes, loose) => [
  fullBoxes > 0 && `${fullBoxes} ${fullBoxes === 1 ? 'box' : 'boxes'}`,
  loose > 0 && `${loose} loose`
].filter(Boolean).join(' + ');

// Boxes and MDF sheets for `quantity` blocks of this work order's size, or null when it
// isn't a Photo Block size we know.
export const photoBlockPacking = (wo, quantity) => {
  const spec = photoBlockSize(wo);
  const qty = Number(quantity) || 0;
  if (!spec || qty <= 0) return null;
  return {
    perBox: spec.perBox,
    fullBoxes: Math.floor(qty / spec.perBox),
    loose: qty % spec.perBox,
    sheets: qty / spec.perSheet
  };
};

// MDF cutting list for [{ wo, quantity }]: one group per board, one row per block size in
// whole sheets
// (work orders of the same size are added together, listed in serialNames). Non-block
// lines are left out.
export const photoBlockCuttingList = (entries) => {
  const boards = new Map();
  entries.forEach(({ wo, quantity }) => {
    const spec = photoBlockSize(wo);
    const qty = Number(quantity) || 0;
    const board = PHOTO_BLOCK_BOARDS[(/^PB-([A-Z]+)-/i.exec(wo.partName || '') || [])[1]?.toUpperCase()];
    if (!spec || !board || qty <= 0) return;

    if (!boards.has(board.code)) boards.set(board.code, { ...board, sizes: new Map() });
    const sizes = boards.get(board.code).sizes;
    const row = sizes.get(spec.label) || { size: spec.label, perSheet: spec.perSheet, perBox: spec.perBox, blocks: 0, serialNames: [] };
    row.blocks += qty;
    if (wo.serialName) row.serialNames.push(wo.serialName);
    sizes.set(spec.label, row);
  });

  return [...boards.values()].map(({ sizes, ...board }) => {
    // Each size is cut from its own sheets, so round up per size. The epsilon stops float
    // noise (e.g. 238 / 119) from adding a sheet.
    const rows = [...sizes.values()].map(row => {
      const exactSheets = row.blocks / row.perSheet;
      return {
        ...row,
        fullBoxes: Math.floor(row.blocks / row.perBox),
        loose: row.blocks % row.perBox,
        exactSheets,
        sheets: Math.ceil(exactSheets - 1e-9)
      };
    });
    return {
      ...board,
      rows,
      blocks: rows.reduce((sum, row) => sum + row.blocks, 0),
      fullBoxes: rows.reduce((sum, row) => sum + row.fullBoxes, 0),
      loose: rows.reduce((sum, row) => sum + row.loose, 0),
      sheets: rows.reduce((sum, row) => sum + row.sheets, 0)
    };
  });
};

// Cards replace the table at this width and below (iPad portrait included). The CSS
// media queries in ReportProduction.css use the same 900px.
export const CARD_LAYOUT_QUERY = '(max-width: 900px)';

// Over-report guards. Above OVER_PLAN_CONFIRM_RATIO x what's left the operator must
// tick a second confirmation; above REPORT_LIMIT_RATIO x planned Priority refuses the
// line outright, so it's blocked before submitting.
export const OVER_PLAN_CONFIRM_RATIO = 1.2;
export const REPORT_LIMIT_RATIO = 10;

// How many good units are above what's left on the work order (0 when within plan)
export const overPlanBy = (wo, quantity) => Math.max(0, (Number(quantity) || 0) - remainingFor(wo));

// True when the good quantity is far enough over what's left to need a second confirmation
export const needsOverPlanConfirm = (wo, quantity) => {
  const qty = Number(quantity) || 0;
  return qty > 0 && qty > remainingFor(wo) * OVER_PLAN_CONFIRM_RATIO;
};

// True when good + rejects exceed what Priority accepts for the work order
export const exceedsReportLimit = (wo, quantity, rejectQuantity) =>
  (Number(quantity) || 0) + (Number(rejectQuantity) || 0) > (Number(wo.quantity) || 0) * REPORT_LIMIT_RATIO;

// "just now", "42 min ago", "3 h ago", "2 days ago"
export const formatAge = (date, now = new Date()) => {
  const ms = now.getTime() - new Date(date).getTime();
  if (isNaN(ms)) return '';
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
};

// Local calendar day as YYYY-MM-DD, the format of <input type="date">
export const localDayKey = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Everyone who reported lines in a saved report, in first-seen order
export const reportEmployees = (items) =>
  [...new Set((items || []).map(item => item.employeeName).filter(Boolean))];

const matchesText = (fields, term) =>
  fields.some(field => field !== undefined && field !== null && String(field).toLowerCase().includes(term));

// History reports matching a free-text search (report no, WO, part, employee) and a
// YYYY-MM-DD day ('' = any day)
export const filterHistoryReports = (reports, search, day) => {
  const term = search.trim().toLowerCase();
  return reports.filter(report => {
    if (day && localDayKey(report.created_at) !== day) return false;
    if (!term) return true;
    return matchesText([report.report_no], term) || (report.items || []).some(item =>
      matchesText([item.serialName, item.partName, item.partDescription, item.employeeName, item.al], term));
  });
};

// Reported work orders matching the same search and day
export const filterReportedWorkOrders = (workOrders, search, day) => {
  const term = search.trim().toLowerCase();
  return workOrders.filter(wo => {
    if (day && localDayKey(wo.reportedAt) !== day) return false;
    return !term || matchesText([wo.serialName, wo.partName, wo.partDescription, wo.reportNo], term);
  });
};
